import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import "../../assets/css/occasion.css";
import {
  addHotel,
  updateHotel,
  getHotelsByEventId,
  deleteHotel,
} from "../../server/Api";
import HotelRoomPreviewModal from "../../components/HotelRoomPreviewModal";
import {
  getBlockCategoryTitle,
  getBlockCategoryBaseTitle,
  getBlockGroupKey,
  getCategoryAccentClass,
  computeHotelMetrics,
  buildHotelRoomGroups,
  CURRENCY_OPTIONS,
  formatRoomPrice,
  formatStayPrice,
  normalizeNights,
  generateRoomNumbersFromStart,
  getCurrencySymbol,
  computeHotelPricing,
} from "../../utils/hotelRoomUtils";
import {
  validateRequiredFields,
  firstErrorMessage,
  focusFirstInvalidField,
  withInvalidClass,
} from "../../utils/formValidation";

const ROOM_TYPES = [
  "Standard",
  "Deluxe",
  "Premium",
  "Executive",
  "Suite",
];
const BED_TYPES = ["Single", "Twin", "Double", "Queen", "King"];

const emptyForm = () => ({
  hotelName: "",
  totalRooms: "",
  totalNights: "1",
  roomNumbers: [],
  standardCheckInTime: "14:00",
  standardCheckOutTime: "11:00",
  address: {
    line1: "",
    line2: "",
    city: "",
    state: "",
    pincode: "",
    country: "",
  },
});

const emptyBulkBlock = () => ({
  roomType: "Deluxe",
  bedType: "King",
  quantity: "",
  startNumber: "",
  currency: "INR",
  price: "",
});

const emptyRoomDetails = () => ({
  roomType: "",
  bedType: "",
  bedSize: "",
  currency: "INR",
  price: "",
});

const HotelMaster = () => {
  const selectedEvent = useSelector((state) => state.auth.selectedEvent);
  const user = useSelector((state) => state.auth.user);
  const roomNumberSnapshotRef = useRef("");
  const roomPriceSnapshotRef = useRef("");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [hotels, setHotels] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editHotelId, setEditHotelId] = useState(null);
  const [formData, setFormData] = useState(emptyForm());
  const [roomIdMap, setRoomIdMap] = useState([]);
  const [roomStatusMap, setRoomStatusMap] = useState([]);
  const [roomDetailsMap, setRoomDetailsMap] = useState([]);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [addressDraft, setAddressDraft] = useState(emptyForm().address);
  const [preview, setPreview] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    hotelId: null,
    hotelName: "",
  });
  const [bulkBlock, setBulkBlock] = useState(emptyBulkBlock());
  const [editingBlockKey, setEditingBlockKey] = useState(null);
  const [editingBlockIndices, setEditingBlockIndices] = useState([]);
  const [priceDrafts, setPriceDrafts] = useState({});

  const totalRoomCount = formData.roomNumbers.length;
  const totalNights = normalizeNights(formData.totalNights);

  const formRoomsForPricing = formData.roomNumbers.map((roomNumber, index) => {
    const details = roomDetailsMap[index] || emptyRoomDetails();
    return {
      roomNumber,
      currency: details.currency || "INR",
      price: details.price,
    };
  });
  const formPricing = computeHotelPricing(formRoomsForPricing, totalNights);

  const groupedRoomBlocks = (() => {
    const groups = new Map();
    formData.roomNumbers.forEach((roomNumber, index) => {
      const details = roomDetailsMap[index] || emptyRoomDetails();
      const key = getBlockGroupKey(
        details.roomType,
        details.bedType,
        details.currency,
        details.price
      );
      if (!groups.has(key)) {
        groups.set(key, {
          key,
          roomType: details.roomType || "",
          bedType: details.bedType || "",
          currency: details.currency || "INR",
          price: details.price ?? "",
          baseTitle: getBlockCategoryBaseTitle(
            details.roomType,
            details.bedType
          ),
          priceLabel: formatRoomPrice(details.currency, details.price),
          title: getBlockCategoryTitle(
            details.roomType,
            details.bedType,
            details.currency,
            details.price
          ),
          items: [],
        });
      }
      groups.get(key).items.push({
        index,
        roomNumber,
        details,
        status: roomStatusMap[index] || "available",
        roomId: roomIdMap[index],
      });
    });
    return Array.from(groups.values());
  })();

  const fetchHotels = async () => {
    try {
      if (!selectedEvent?.eventId) return;
      const res = await axios.get(getHotelsByEventId(selectedEvent.eventId));
      if (res.status === 200 && res.data.data) {
        setHotels(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching hotels:", error);
      toast.error("Failed to fetch hotels.");
    }
  };

  useEffect(() => {
    fetchHotels();
  }, [selectedEvent?.eventId]);

  const hasAddress = (address = {}) =>
    Boolean(
      address.line1 ||
        address.line2 ||
        address.city ||
        address.state ||
        address.pincode ||
        address.country
    );

  const formatHotelAddress = (address = {}) => {
    const line = [address.line1, address.line2].filter(Boolean).join(", ");
    const locality = [address.city, address.state, address.pincode]
      .filter(Boolean)
      .join(", ");
    const parts = [line, locality, address.country].filter(Boolean);
    return parts.join(" · ");
  };

  const getHotelRoomGroups = (hotel) =>
    buildHotelRoomGroups(hotel.rooms || []);

  const openAddressModal = () => {
    setAddressDraft({ ...formData.address });
    setAddressModalOpen(true);
  };

  const closeAddressModal = () => {
    setAddressModalOpen(false);
  };

  const handleAddressDraftChange = (e) => {
    const { name, value } = e.target;
    setAddressDraft((prev) => ({ ...prev, [name]: value }));
  };

  const saveAddressModal = () => {
    setFormData((prev) => ({
      ...prev,
      address: { ...addressDraft },
    }));
    setAddressModalOpen(false);
  };

  const findDuplicateRoomNumber = (roomNumber, excludeIndex = null) => {
    const key = String(roomNumber || "")
      .trim()
      .toLowerCase();
    if (!key) return null;
    const duplicateIndex = formData.roomNumbers.findIndex((value, index) => {
      if (excludeIndex != null && index === excludeIndex) return false;
      return String(value || "").trim().toLowerCase() === key;
    });
    return duplicateIndex >= 0 ? duplicateIndex : null;
  };

  const parseBulkBlockPrice = () => {
    if (!String(bulkBlock.currency || "").trim()) {
      toast.error("Select a currency");
      return null;
    }
    const priceValue = String(bulkBlock.price || "").trim();
    if (!priceValue) {
      toast.error("Enter the room price");
      return null;
    }
    const parsedPrice = Number(priceValue);
    if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
      toast.error("Enter a valid room price");
      return null;
    }
    return parsedPrice;
  };

  const clearBlockEdit = () => {
    setEditingBlockKey(null);
    setEditingBlockIndices([]);
    setBulkBlock((prev) => ({
      ...prev,
      quantity: "",
      startNumber: "",
      price: "",
    }));
  };

  const startEditingBlock = (group) => {
    const indices = group.items.map((item) => item.index);
    const sortedItems = [...group.items].sort((a, b) =>
      String(a.roomNumber || "").localeCompare(
        String(b.roomNumber || ""),
        undefined,
        { numeric: true, sensitivity: "base" }
      )
    );
    const startNumber = String(sortedItems[0]?.roomNumber || "");

    setEditingBlockKey(group.key);
    setEditingBlockIndices(indices);
    setBulkBlock({
      roomType: group.roomType || "Deluxe",
      bedType: group.bedType || "King",
      quantity: String(group.items.length),
      startNumber,
      currency: group.currency || "INR",
      price:
        group.price !== "" && group.price != null ? String(group.price) : "",
    });

    window.requestAnimationFrame(() => {
      document
        .querySelector(".hotel-inventory-form")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  };

  const appendBulkRooms = () => {
    const quantity = parseInt(String(bulkBlock.quantity).trim(), 10);
    if (Number.isNaN(quantity) || quantity < 1) {
      toast.error("Enter the number of rooms for this block");
      return;
    }
    if (!String(bulkBlock.startNumber).trim()) {
      toast.error("Enter the starting room number");
      return;
    }
    const parsedPrice = parseBulkBlockPrice();
    if (parsedPrice == null) return;

    const generated = generateRoomNumbersFromStart(
      bulkBlock.startNumber,
      quantity
    );
    if (!generated.length) {
      toast.error("Enter a valid starting room number and quantity");
      return;
    }

    const duplicates = generated.filter(
      (num) => findDuplicateRoomNumber(num) != null
    );
    if (duplicates.length) {
      toast.error(`Room number(s) already exist: ${duplicates.join(", ")}`);
      return;
    }

    const blockDetails = {
      roomType: bulkBlock.roomType,
      bedType: bulkBlock.bedType,
      bedSize: bulkBlock.bedType,
      currency: bulkBlock.currency || "INR",
      price: parsedPrice,
    };
    const nextNumbers = [...formData.roomNumbers, ...generated];
    setFormData((prev) => ({
      ...prev,
      totalRooms: String(nextNumbers.length),
      roomNumbers: nextNumbers,
    }));
    setRoomIdMap((prev) => [...prev, ...generated.map(() => null)]);
    setRoomStatusMap((prev) => [...prev, ...generated.map(() => "available")]);
    setRoomDetailsMap((prev) => [
      ...prev,
      ...generated.map(() => ({ ...blockDetails })),
    ]);
    setBulkBlock((prev) => ({
      ...prev,
      quantity: "",
      startNumber: "",
      price: "",
    }));
    toast.success(
      `Added ${generated.length} room(s) — ${getBlockCategoryTitle(
        bulkBlock.roomType,
        bulkBlock.bedType,
        bulkBlock.currency,
        parsedPrice
      )}`
    );
  };

  const updateBulkRooms = () => {
    if (!editingBlockKey || !editingBlockIndices.length) {
      appendBulkRooms();
      return;
    }

    const quantity = parseInt(String(bulkBlock.quantity).trim(), 10);
    if (Number.isNaN(quantity) || quantity < 1) {
      toast.error("Enter the number of rooms for this block");
      return;
    }
    if (!String(bulkBlock.startNumber).trim()) {
      toast.error("Enter the starting room number");
      return;
    }
    const parsedPrice = parseBulkBlockPrice();
    if (parsedPrice == null) return;

    const sortedIndices = [...editingBlockIndices].sort((a, b) => a - b);
    const occupiedInBlock = sortedIndices.filter(
      (index) => roomStatusMap[index] === "occupied"
    );
    if (quantity < occupiedInBlock.length) {
      toast.error(
        `Cannot set fewer than ${occupiedInBlock.length} room(s) — some are already assigned`
      );
      return;
    }

    const blockDetails = {
      roomType: bulkBlock.roomType,
      bedType: bulkBlock.bedType,
      bedSize: bulkBlock.bedType,
      currency: bulkBlock.currency || "INR",
      price: parsedPrice,
    };

    let nextNumbers = [...formData.roomNumbers];
    let nextIds = [...roomIdMap];
    let nextStatuses = [...roomStatusMap];
    let nextDetails = [...roomDetailsMap];

    sortedIndices.forEach((index) => {
      nextDetails[index] = { ...blockDetails };
    });

    if (quantity < sortedIndices.length) {
      const removable = [...sortedIndices]
        .reverse()
        .filter((index) => nextStatuses[index] !== "occupied")
        .slice(0, sortedIndices.length - quantity);
      const removeSet = new Set(removable);
      nextNumbers = nextNumbers.filter((_, index) => !removeSet.has(index));
      nextIds = nextIds.filter((_, index) => !removeSet.has(index));
      nextStatuses = nextStatuses.filter((_, index) => !removeSet.has(index));
      nextDetails = nextDetails.filter((_, index) => !removeSet.has(index));
    } else if (quantity > sortedIndices.length) {
      const addCount = quantity - sortedIndices.length;
      const poolSize =
        sortedIndices.length + addCount + Math.max(nextNumbers.length, 8);
      const pool = generateRoomNumbersFromStart(
        bulkBlock.startNumber,
        poolSize
      );
      if (!pool.length) {
        toast.error("Enter a valid starting room number to add more rooms");
        return;
      }
      const generated = [];
      for (const candidate of pool) {
        if (generated.length >= addCount) break;
        const key = String(candidate).trim().toLowerCase();
        const exists =
          nextNumbers.some(
            (value) => String(value || "").trim().toLowerCase() === key
          ) || generated.some((value) => value.toLowerCase() === key);
        if (!exists) generated.push(candidate);
      }
      if (generated.length < addCount) {
        toast.error("Could not generate unique room numbers for the extra rooms");
        return;
      }
      nextNumbers = [...nextNumbers, ...generated];
      nextIds = [...nextIds, ...generated.map(() => null)];
      nextStatuses = [...nextStatuses, ...generated.map(() => "available")];
      nextDetails = [
        ...nextDetails,
        ...generated.map(() => ({ ...blockDetails })),
      ];
    }

    setFormData((prev) => ({
      ...prev,
      totalRooms: String(nextNumbers.length),
      roomNumbers: nextNumbers,
    }));
    setRoomIdMap(nextIds);
    setRoomStatusMap(nextStatuses);
    setRoomDetailsMap(nextDetails);
    clearBlockEdit();
    toast.success(
      `Updated block — ${getBlockCategoryTitle(
        bulkBlock.roomType,
        bulkBlock.bedType,
        bulkBlock.currency,
        parsedPrice
      )}`
    );
  };

  const handleBlockAction = () => {
    if (editingBlockKey) updateBulkRooms();
    else appendBulkRooms();
  };

  const handleRoomNumberFocus = (index) => {
    roomNumberSnapshotRef.current = formData.roomNumbers[index] || "";
  };

  const handleRoomNumberChange = (index, value) => {
    setFormData((prev) => {
      const roomNumbers = [...prev.roomNumbers];
      roomNumbers[index] = value;
      return { ...prev, roomNumbers };
    });
  };

  const handleRoomNumberBlur = (index, value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) return;

    const duplicateIndex = findDuplicateRoomNumber(trimmed, index);
    if (duplicateIndex != null) {
      toast.error(`Room number "${trimmed}" is already used in this hotel`);
      setFormData((prev) => {
        const roomNumbers = [...prev.roomNumbers];
        roomNumbers[index] = roomNumberSnapshotRef.current;
        return { ...prev, roomNumbers };
      });
      return;
    }

    if (trimmed !== value) {
      setFormData((prev) => {
        const roomNumbers = [...prev.roomNumbers];
        roomNumbers[index] = trimmed;
        return { ...prev, roomNumbers };
      });
    }
  };

  const clearPriceDraft = (index) => {
    setPriceDrafts((prev) => {
      if (prev[index] === undefined) return prev;
      const next = { ...prev };
      delete next[index];
      return next;
    });
  };

  const handleRoomPriceFocus = (index) => {
    const details = roomDetailsMap[index] || emptyRoomDetails();
    const current =
      details.price !== "" && details.price != null ? String(details.price) : "";
    roomPriceSnapshotRef.current = current;
    setPriceDrafts((prev) => ({ ...prev, [index]: current }));
  };

  const handleRoomPriceChange = (index, value) => {
    // Keep drafts out of grouping until blur so the input does not remount
    setPriceDrafts((prev) => ({ ...prev, [index]: value }));
  };

  const handleRoomPriceBlur = (index, value) => {
    const trimmed = String(value || "").trim();
    if (!trimmed) {
      toast.error("Room price is required");
      clearPriceDraft(index);
      return;
    }
    const parsed = Number(trimmed);
    if (Number.isNaN(parsed) || parsed < 0) {
      toast.error("Enter a valid room price");
      clearPriceDraft(index);
      return;
    }
    setRoomDetailsMap((prev) => {
      const next = [...prev];
      const current = next[index] || emptyRoomDetails();
      next[index] = { ...current, price: parsed };
      return next;
    });
    clearPriceDraft(index);
    if (editingBlockKey) {
      setEditingBlockKey(null);
      setEditingBlockIndices([]);
    }
  };

  const handleRemoveRoom = (index) => {
    if (roomStatusMap[index] === "occupied") {
      toast.error("Assigned rooms cannot be removed");
      return;
    }

    setFormData((prev) => {
      const roomNumbers = prev.roomNumbers.filter((_, i) => i !== index);
      return {
        ...prev,
        roomNumbers,
        totalRooms: String(roomNumbers.length),
      };
    });
    setRoomIdMap((prev) => prev.filter((_, i) => i !== index));
    setRoomStatusMap((prev) => prev.filter((_, i) => i !== index));
    setRoomDetailsMap((prev) => prev.filter((_, i) => i !== index));
    if (editingBlockKey) clearBlockEdit();
  };

  const resetForm = () => {
    setFormData(emptyForm());
    setRoomIdMap([]);
    setRoomStatusMap([]);
    setRoomDetailsMap([]);
    setIsEditing(false);
    setEditHotelId(null);
    setAddressDraft(emptyForm().address);
    setAddressModalOpen(false);
    setBulkBlock(emptyBulkBlock());
    setEditingBlockKey(null);
    setEditingBlockIndices([]);
    setPriceDrafts({});
  };
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedEvent?.eventId) {
      toast.error("Event ID missing!");
      return;
    }
    if (!formData.hotelName.trim()) {
      toast.error("Please enter hotel name");
      return;
    }
    const total = formData.roomNumbers.length;
    if (!total) {
      toast.error("Add at least one room block before saving");
      return;
    }
    if (formData.roomNumbers.some((r) => !String(r).trim())) {
      toast.error("Please enter a room number for every room");
      return;
    }

    const seen = new Set();
    for (let i = 0; i < formData.roomNumbers.length; i += 1) {
      const key = String(formData.roomNumbers[i]).trim().toLowerCase();
      if (seen.has(key)) {
        toast.error(
          `Duplicate room number "${formData.roomNumbers[i].trim()}" is not allowed for the same hotel`
        );
        return;
      }
      seen.add(key);
    }

    const rooms = formData.roomNumbers.map((roomNumber, index) => {
      const details = roomDetailsMap[index] || emptyRoomDetails();
      const parsedPrice =
        details.price !== "" && details.price != null
          ? Number(details.price)
          : null;
      const payload = {
        roomNumber: String(roomNumber).trim(),
        roomType: details.roomType || "",
        bedType: details.bedType || details.bedSize || "",
        floorPreference: "",
        roomFacing: "",
        roomPreference: "",
        bedSize: details.bedSize || details.bedType || "",
        currency: details.currency || "INR",
        price:
          parsedPrice != null && !Number.isNaN(parsedPrice)
            ? parsedPrice
            : null,
      };
      if (roomIdMap[index]) payload._id = roomIdMap[index];
      return payload;
    });

    const payload = {
      eventId: selectedEvent.eventId,
      hotelName: formData.hotelName.trim(),
      address: formData.address,
      standardCheckInTime: formData.standardCheckInTime || "14:00",
      standardCheckOutTime: formData.standardCheckOutTime || "11:00",
      totalRooms: total,
      totalNights: normalizeNights(formData.totalNights),
      rooms,
      enteredBy: user?.name || user?.email || "system",
      modifiedBy: user?.name || user?.email || "system",
    };

    try {
      if (isEditing && editHotelId) {
        const res = await axios.put(updateHotel(editHotelId), payload);
        if (res.status === 200) {
          toast.success("Hotel updated successfully!");
          resetForm();
          await fetchHotels();
        }
      } else {
        const res = await axios.post(addHotel(), payload);
        if (res.status === 200 || res.status === 201) {
          toast.success("Hotel saved successfully!");
          resetForm();
          await fetchHotels();
        }
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to save hotel");
    }
  };

  const handleEdit = (hotel) => {
    const rooms = Array.isArray(hotel.rooms) ? hotel.rooms : [];
    setIsEditing(true);
    setEditHotelId(hotel._id);
    setFormData({
      hotelName: hotel.hotelName || "",
      totalRooms: String(hotel.totalRooms || rooms.length || ""),
      totalNights: String(normalizeNights(hotel.totalNights)),
      standardCheckInTime: hotel.standardCheckInTime || "14:00",
      standardCheckOutTime: hotel.standardCheckOutTime || "11:00",
      roomNumbers: rooms.map((r) => String(r.roomNumber || "")),
      address: {
        line1: hotel.address?.line1 || "",
        line2: hotel.address?.line2 || "",
        city: hotel.address?.city || "",
        state: hotel.address?.state || "",
        pincode: hotel.address?.pincode || "",
        country: hotel.address?.country || "",
      },
    });
    setRoomIdMap(rooms.map((r) => (r._id ? String(r._id) : null)));
    setRoomStatusMap(
      rooms.map((r) => (r.status === "occupied" ? "occupied" : "available"))
    );
    setRoomDetailsMap(
      rooms.map((r) => ({
        roomType: r.roomType || "",
        bedType: r.bedType || r.bedSize || "",
        bedSize: r.bedSize || r.bedType || "",
        currency: r.currency || "INR",
        price: r.price != null && r.price !== "" ? r.price : "",
      }))
    );
    setBulkBlock(emptyBulkBlock());
    setEditingBlockKey(null);
    setEditingBlockIndices([]);
    setPriceDrafts({});
    setIsSidebarExpanded(false);
    window.requestAnimationFrame(() => {
      document
        .getElementById("hotel-edit-form")
        ?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  const handleDelete = async () => {
    if (!deleteConfirmation.hotelId) return;
    try {
      const res = await axios.delete(deleteHotel(deleteConfirmation.hotelId));
      if (res.status === 200) {
        toast.success("Hotel deleted successfully!");
        if (editHotelId === deleteConfirmation.hotelId) resetForm();
        await fetchHotels();
      }
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || "Failed to delete hotel");
    } finally {
      setDeleteConfirmation({ isOpen: false, hotelId: null, hotelName: "" });
    }
  };

  const filteredHotels = hotels.filter((hotel) =>
    String(hotel.hotelName || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const openPreviewFromHotel = (hotel) => {
    setPreview({
      viewMode: "single",
      hotelName: hotel.hotelName,
      address: hotel.address,
      rooms: hotel.rooms || [],
      metrics: hotel.metrics,
      totalNights: normalizeNights(hotel.totalNights),
      isDraft: false,
    });
  };

  const openPreviewAllHotels = () => {
    if (!hotels.length) {
      toast.error("No hotels configured for this event yet");
      return;
    }
    setPreview({
      viewMode: "event",
      hotels,
    });
  };

  const openPreviewFromForm = () => {
    const rooms = formData.roomNumbers
      .map((roomNumber, index) => {
        const details = roomDetailsMap[index] || emptyRoomDetails();
        if (!String(roomNumber || "").trim()) return null;
        return {
          roomNumber: String(roomNumber).trim(),
          roomType: details.roomType || "",
          bedType: details.bedType || details.bedSize || "",
          currency: details.currency || "INR",
          price: details.price ?? "",
          status: roomStatusMap[index] === "occupied" ? "occupied" : "available",
        };
      })
      .filter(Boolean);

    if (!rooms.length) {
      toast.error("Add at least one room to preview the layout");
      return;
    }

    setPreview({
      viewMode: "single",
      hotelName: formData.hotelName || "Untitled Hotel",
      address: formData.address,
      rooms,
      metrics: computeHotelMetrics(rooms),
      totalNights,
      isDraft: !isEditing || !editHotelId,
    });
  };

  return (
    <>
      <div className="container-fluid accommodation-page hotel-master-page">
        <div
          className={`occasion-top occasion-top-with-sidebar row g-4 align-items-center${
            isSidebarExpanded ? " expanded-sidebar" : ""
          }`}
        >
          <div className="col-lg-9 hotel-master-page-title-row">
            <h2 className="mb-0">Hotel Master</h2>
            <div className="hotel-master-page-title-center">
              {(totalRoomCount > 0 || totalNights > 1) && (
                <div className="hotel-total-metrics">
                  {totalRoomCount > 0 && (
                    <div className="hotel-total-box">
                      <span className="hotel-total-label">Total Rooms</span>
                      <span className="hotel-total-count">{totalRoomCount}</span>
                    </div>
                  )}
                  <div className="hotel-total-box hotel-nights-box">
                    <span className="hotel-total-label">Total Nights</span>
                    <span className="hotel-total-count">{totalNights}</span>
                  </div>
                  {formPricing.label ? (
                    <div
                      className="hotel-total-box hotel-pricing-box"
                      title={`${formPricing.breakdown} (room rate × nights)`}
                    >
                      <span className="hotel-total-label">Pricing</span>
                      <span className="hotel-total-count hotel-pricing-count">
                        {formPricing.label}
                      </span>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
            <div className="hotel-master-page-title-right">
              {hotels.length > 0 && (
                <button
                  type="button"
                  className="btn hotel-preview-all-btn"
                  onClick={openPreviewAllHotels}
                >
                  <i className="bi bi-buildings" />
                  Preview All Hotels
                </button>
              )}
            </div>
          </div>
          <div className="col-lg-3 occasion-top-search-col">
            <input
              type="text"
              className="form-control mb-3"
              placeholder="Search hotel..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="Occasion_Master">
          <div
            className={`row ${isSidebarExpanded ? "expanded-sidebar" : ""}`}
          >
            <div className="col-lg-9" id="formColumn">
              <div className="form-section" id="hotel-edit-form">
                <form onSubmit={handleSubmit}>
                  <div className="row mb-2 all-page-scroll all-page-scroll1 accomodation-form">
                    <div className="col-12 mb-2">
                      <div className="hotel-room-block-form hotel-details-form">
                        <h6 className="hotel-room-section-title hotel-details-form-title">
                          {isEditing
                            ? `Edit Hotel${
                                formData.hotelName
                                  ? ` — ${formData.hotelName}`
                                  : ""
                              }`
                            : "Add Hotel"}
                        </h6>
                        <div className="hotel-details-form-body">
                          <div className="row g-3 align-items-end">
                            <div className="col-md-6">
                              <label className="form-label">Hotel Name</label>
                              <input
                                type="text"
                                className="form-control"
                                value={formData.hotelName}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    hotelName: e.target.value,
                                  }))
                                }
                                placeholder="Enter hotel name"
                              />
                            </div>
                            <div className="col-md-6">
                              <button
                                type="button"
                                className={`btn hotel-address-btn${
                                  hasAddress(formData.address)
                                    ? " has-address"
                                    : ""
                                }`}
                                onClick={openAddressModal}
                              >
                                <i
                                  className={`bi ${
                                    hasAddress(formData.address)
                                      ? "bi-pencil-square"
                                      : "bi-geo-alt"
                                  }`}
                                  aria-hidden="true"
                                />
                                {hasAddress(formData.address)
                                  ? "Edit Address"
                                  : "Add Address"}
                              </button>
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">
                                Standard Check-in
                              </label>
                              <input
                                type="time"
                                className="form-control"
                                value={formData.standardCheckInTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    standardCheckInTime: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">
                                Standard Check-out
                              </label>
                              <input
                                type="time"
                                className="form-control"
                                value={formData.standardCheckOutTime}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    standardCheckOutTime: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="col-md-3">
                              <label className="form-label">
                                Total Nights
                              </label>
                              <input
                                type="number"
                                min="1"
                                step="1"
                                className="form-control"
                                value={formData.totalNights}
                                onChange={(e) =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    totalNights: e.target.value,
                                  }))
                                }
                                onBlur={() =>
                                  setFormData((prev) => ({
                                    ...prev,
                                    totalNights: String(
                                      normalizeNights(prev.totalNights)
                                    ),
                                  }))
                                }
                                placeholder="e.g. 3"
                                title="Stay length used to calculate total room pricing"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="col-12 mb-2">
                      <div className="hotel-room-block-form hotel-inventory-form">
                        <div className="hotel-inventory-fields">
                          <div className="row g-2 align-items-end">
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">Room Type</label>
                              <select
                                className="form-select"
                                value={bulkBlock.roomType}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    roomType: e.target.value,
                                  }))
                                }
                              >
                                {ROOM_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">Bed Type</label>
                              <select
                                className="form-select"
                                value={bulkBlock.bedType}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    bedType: e.target.value,
                                  }))
                                }
                              >
                                {BED_TYPES.map((type) => (
                                  <option key={type} value={type}>
                                    {type}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">No. of Rooms</label>
                              <input
                                type="number"
                                min="1"
                                className="form-control"
                                placeholder="e.g. 20"
                                value={bulkBlock.quantity}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    quantity: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">
                                Starting Room No.
                              </label>
                              <input
                                type="text"
                                className="form-control"
                                placeholder="101 or Lotus / Jasmine 3"
                                value={bulkBlock.startNumber}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    startNumber: e.target.value,
                                  }))
                                }
                                title="Numbers (101) or names (Lotus, Jasmine 3)"
                              />
                            </div>
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">Currency</label>
                              <select
                                className="form-select"
                                value={bulkBlock.currency}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    currency: e.target.value,
                                  }))
                                }
                              >
                                {CURRENCY_OPTIONS.map((item) => (
                                  <option key={item.code} value={item.code}>
                                    {item.label}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="col-6 col-md-3 col-xl">
                              <label className="form-label">
                                Price / night
                              </label>
                              <input
                                type="number"
                                min="0"
                                step="0.01"
                                className="form-control"
                                placeholder="e.g. 7500"
                                value={bulkBlock.price}
                                onChange={(e) =>
                                  setBulkBlock((prev) => ({
                                    ...prev,
                                    price: e.target.value,
                                  }))
                                }
                              />
                            </div>
                            <div className="col-12 col-md-auto hotel-inventory-actions">
                              <button
                                type="button"
                                className={`btn hotel-add-block-btn${
                                  editingBlockKey ? " is-updating" : ""
                                }`}
                                onClick={handleBlockAction}
                              >
                                {editingBlockKey ? "Update Block" : "Add Block"}
                              </button>
                              {editingBlockKey && (
                                <button
                                  type="button"
                                  className="btn hotel-cancel-block-btn"
                                  onClick={clearBlockEdit}
                                  title="Cancel block edit"
                                >
                                  Cancel
                                </button>
                              )}
                              <button
                                type="button"
                                className="btn hotel-inventory-preview-btn"
                                onClick={openPreviewFromForm}
                                disabled={totalRoomCount === 0}
                                title={
                                  totalRoomCount === 0
                                    ? "Add rooms to preview layout"
                                    : "Preview room layout"
                                }
                                aria-label="Preview room layout"
                              >
                                <i className="bi bi-eye" aria-hidden="true" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    {groupedRoomBlocks.length > 0 && (
                      <div className="col-12 mb-2">
                        <div className="hotel-room-blocks-below">
                          <div className="hotel-room-blocks-below-head">
                            <h6 className="hotel-room-blocks-below-title mb-0">
                              Room inventory by type
                            </h6>
                            <span className="hotel-room-blocks-below-meta">
                              {totalRoomCount} room
                              {totalRoomCount === 1 ? "" : "s"}
                              {totalNights > 1
                                ? ` · ${totalNights} nights`
                                : ""}{" "}
                              · edit numbers & prices below
                            </span>
                          </div>
                          {groupedRoomBlocks.map((group) => {
                            const nightPriceLabel = formatRoomPrice(
                              group.currency,
                              group.price
                            );
                            const stayPriceLabel = formatStayPrice(
                              group.currency,
                              group.price,
                              totalNights
                            );
                            const isBlockEditing =
                              editingBlockKey === group.key;
                            return (
                            <div
                              className={`hotel-room-block-group ${getCategoryAccentClass(
                                group.roomType,
                                group.bedType
                              )}${isBlockEditing ? " is-editing-block" : ""}`}
                              key={group.key}
                            >
                              <div className="hotel-room-block-head">
                                <h6 className="hotel-room-block-title">
                                  <span className="hotel-room-block-title-text">
                                    {group.baseTitle || group.title}
                                  </span>
                                  {nightPriceLabel ? (
                                    <span
                                      className="hotel-room-block-price-badge"
                                      title={
                                        totalNights > 1 && stayPriceLabel
                                          ? `${nightPriceLabel}/night · ${stayPriceLabel} stay`
                                          : `${nightPriceLabel} per night`
                                      }
                                    >
                                      {nightPriceLabel}
                                      <em>/night</em>
                                    </span>
                                  ) : null}
                                </h6>
                                <div className="hotel-room-block-head-actions">
                                  <button
                                    type="button"
                                    className="hotel-room-block-edit-btn"
                                    onClick={() => startEditingBlock(group)}
                                    title="Edit this room block"
                                    aria-label={`Edit ${
                                      group.baseTitle || group.title
                                    } block`}
                                  >
                                    <i
                                      className="bi bi-pencil-square"
                                      aria-hidden="true"
                                    />
                                  </button>
                                  <span className="hotel-room-block-count">
                                    {group.items.length} room
                                    {group.items.length === 1 ? "" : "s"}
                                    {totalNights > 1 && stayPriceLabel
                                      ? ` · ${stayPriceLabel} stay`
                                      : ""}
                                  </span>
                                </div>
                              </div>
                              <div className="hotel-room-pill-grid">
                                {group.items.map((item, seqIndex) => {
                                  const isOccupied =
                                    isEditing && item.status === "occupied";
                                  const roomCurrency =
                                    item.details?.currency || group.currency;
                                  const roomPrice =
                                    item.details?.price ?? group.price;
                                  const currencySymbol =
                                    getCurrencySymbol(roomCurrency);
                                  const draftPrice = priceDrafts[item.index];
                                  const priceInputValue =
                                    draftPrice !== undefined
                                      ? draftPrice
                                      : roomPrice === "" || roomPrice == null
                                        ? ""
                                        : String(roomPrice);
                                  const stayLabel = formatStayPrice(
                                    roomCurrency,
                                    roomPrice,
                                    totalNights
                                  );
                                  return (
                                    <div
                                      className={`hotel-room-pill-item is-${
                                        isOccupied ? "occupied" : "available"
                                      }`}
                                      key={`room-card-${item.index}`}
                                    >
                                      <div className="hotel-room-pill-top">
                                        <span className="hotel-room-pill-label">
                                          Room
                                        </span>
                                        {!isOccupied ? (
                                          <button
                                            type="button"
                                            className="hotel-room-pill-remove"
                                            onClick={() =>
                                              handleRemoveRoom(item.index)
                                            }
                                            title="Remove room"
                                            aria-label={`Remove room ${
                                              seqIndex + 1
                                            }`}
                                          >
                                            <i
                                              className="bi bi-x"
                                              aria-hidden="true"
                                            />
                                          </button>
                                        ) : (
                                          <span
                                            className="hotel-room-pill-status"
                                            title="Assigned to a guest"
                                          >
                                            Taken
                                          </span>
                                        )}
                                      </div>
                                      <input
                                        id={`room-input-${item.index}`}
                                        type="text"
                                        className="hotel-room-pill-input"
                                        value={item.roomNumber || ""}
                                        onChange={(e) =>
                                          handleRoomNumberChange(
                                            item.index,
                                            e.target.value
                                          )
                                        }
                                        onFocus={() =>
                                          handleRoomNumberFocus(item.index)
                                        }
                                        onBlur={(e) =>
                                          handleRoomNumberBlur(
                                            item.index,
                                            e.target.value
                                          )
                                        }
                                        disabled={isOccupied}
                                        placeholder="Room name / no."
                                        title={
                                          isOccupied
                                            ? `Room ${
                                                item.roomNumber || seqIndex + 1
                                              } — assigned`
                                            : `Edit room ${seqIndex + 1}`
                                        }
                                        aria-label={`Room ${
                                          seqIndex + 1
                                        } number`}
                                      />
                                      <div className="hotel-room-pill-price-row">
                                        <span
                                          className="hotel-room-pill-currency"
                                          title={roomCurrency}
                                        >
                                          {currencySymbol}
                                        </span>
                                        <input
                                          type="text"
                                          inputMode="decimal"
                                          className="hotel-room-pill-price-input"
                                          value={priceInputValue}
                                          onChange={(e) =>
                                            handleRoomPriceChange(
                                              item.index,
                                              e.target.value
                                            )
                                          }
                                          onFocus={() =>
                                            handleRoomPriceFocus(item.index)
                                          }
                                          onBlur={(e) =>
                                            handleRoomPriceBlur(
                                              item.index,
                                              e.target.value
                                            )
                                          }
                                          disabled={isOccupied}
                                          placeholder="0"
                                          title={
                                            isOccupied
                                              ? "Price locked for assigned room"
                                              : "Edit price per night"
                                          }
                                          aria-label={`Room ${
                                            seqIndex + 1
                                          } price per night`}
                                        />
                                      </div>
                                      {totalNights > 1 && stayLabel ? (
                                        <span
                                          className="hotel-room-pill-stay"
                                          title={`${stayLabel} for ${totalNights} nights`}
                                        >
                                          {stayLabel} / {totalNights}n
                                        </span>
                                      ) : null}
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="hotel-form-actions">
                    <button type="submit" className="btn btn-success btn-save">
                      {isEditing ? "Update" : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-cancel"
                      onClick={resetForm}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>

            <div className="col-lg-3" id="sidebarColumn">
              <div className="event-sidebar">
                <div className="guest-list-toolbar">
                  <h6 className="mb-0 event-inner-hed">Hotels</h6>
                  <button
                    type="button"
                    className="guest-sidebar-toggle"
                    onClick={() => setIsSidebarExpanded((prev) => !prev)}
                    title={isSidebarExpanded ? "Collapse list" : "Expand list"}
                    aria-label={
                      isSidebarExpanded ? "Collapse list" : "Expand list"
                    }
                  >
                    <i
                      className={`bi ${
                        isSidebarExpanded
                          ? "bi-chevron-right"
                          : "bi-chevron-left"
                      }`}
                    ></i>
                  </button>
                </div>
                <hr />
                <div className="all-page-right-scroll accomodation-list">
                  {isSidebarExpanded ? (
                    <div className="table-responsive hotel-expanded-table-wrap">
                      <table className="table table-bordered table-hover hotel-expanded-table">
                        <thead className="thead-dark">
                          <tr>
                            <th>Sn.</th>
                            <th>Hotel</th>
                            <th>Room Categories</th>
                            <th>Total</th>
                            <th>Assigned</th>
                            <th>Available</th>
                            <th>Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredHotels.length > 0 ? (
                            filteredHotels.map((hotel, index) => {
                              const roomGroups = getHotelRoomGroups(hotel);
                              return (
                              <tr
                                key={hotel._id}
                                className={
                                  editHotelId === hotel._id ? "is-editing" : ""
                                }
                              >
                                <td>{index + 1}</td>
                                <td className="hotel-expanded-name">
                                  {hotel.hotelName}
                                </td>
                                <td className="hotel-expanded-categories">
                                  {roomGroups.length ? (
                                    <div className="hotel-expanded-cat-list">
                                      {roomGroups.map((group) => (
                                        <span
                                          key={group.key}
                                          className={`hotel-expanded-cat-chip ${getCategoryAccentClass(
                                            group.roomType,
                                            group.bedType
                                          )}`}
                                        >
                                          <span className="hotel-expanded-cat-name">
                                            {group.baseTitle || group.title}
                                          </span>
                                          {group.priceLabel ? (
                                            <span className="hotel-expanded-cat-price">
                                              {group.priceLabel}
                                            </span>
                                          ) : null}
                                          <strong>{group.items.length}</strong>
                                        </span>
                                      ))}
                                    </div>
                                  ) : (
                                    "—"
                                  )}
                                </td>
                                <td>
                                  {hotel.metrics?.totalRooms ??
                                    hotel.totalRooms}
                                </td>
                                <td>{hotel.metrics?.occupied ?? 0}</td>
                                <td>
                                  {hotel.metrics?.available ??
                                    hotel.totalRooms}
                                </td>
                                <td className="hotel-table-actions">
                                  <button
                                    type="button"
                                    className="btn hotel-card-preview-btn"
                                    onClick={() => openPreviewFromHotel(hotel)}
                                    title="Preview room layout"
                                    aria-label="Preview room layout"
                                  >
                                    <i className="bi bi-eye" aria-hidden="true" />
                                  </button>
                                  <button
                                    type="button"
                                    className="btn edit-btn btn-sm"
                                    onClick={() => handleEdit(hotel)}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-danger btn-sm"
                                    onClick={() =>
                                      setDeleteConfirmation({
                                        isOpen: true,
                                        hotelId: hotel._id,
                                        hotelName: hotel.hotelName,
                                      })
                                    }
                                  >
                                    Delete
                                  </button>
                                </td>
                              </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan="7" className="text-center">
                                No hotels found.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : filteredHotels.length > 0 ? (
                    <div className="acc-detail-list">
                      {filteredHotels.map((hotel, index) => (
                        <div
                          className={`acc-detail-card hotel-card accent-${
                            (index % 5) + 1
                          }${editHotelId === hotel._id ? " is-editing" : ""}`}
                          key={hotel._id}
                        >
                          <div className="acc-detail-head">
                            <strong className="acc-detail-name">
                              {hotel.hotelName}
                            </strong>
                          </div>
                          {hasAddress(hotel.address) ? (
                            <div className="hotel-card-address">
                              <span className="hotel-card-address-label">
                                Address
                              </span>
                              <span className="hotel-card-address-text">
                                {formatHotelAddress(hotel.address)}
                              </span>
                            </div>
                          ) : (
                            <div className="hotel-card-address is-empty">
                              <span className="hotel-card-address-label">
                                Address
                              </span>
                              <span className="hotel-card-address-text">
                                Not added
                              </span>
                            </div>
                          )}
                          <div className="acc-detail-chips hotel-metric-chips">
                            <span className="hotel-metric-chip hotel-metric-total">
                              <em>Total</em>
                              <strong>
                                {hotel.metrics?.totalRooms ?? hotel.totalRooms}
                              </strong>
                            </span>
                            <span className="hotel-metric-chip hotel-metric-nights">
                              <em>Nights</em>
                              <strong>
                                {normalizeNights(hotel.totalNights)}
                              </strong>
                            </span>
                            <span className="hotel-metric-chip hotel-metric-occupied">
                              <em>Assigned</em>
                              <strong>{hotel.metrics?.occupied ?? 0}</strong>
                            </span>
                            <span className="hotel-metric-chip hotel-metric-available">
                              <em>Available</em>
                              <strong>
                                {hotel.metrics?.available ?? hotel.totalRooms}
                              </strong>
                            </span>
                          </div>
                          {(() => {
                            const pricing = computeHotelPricing(
                              hotel.rooms || [],
                              hotel.totalNights
                            );
                            if (!pricing.label) return null;
                            return (
                              <div
                                className="hotel-card-pricing"
                                title={`${pricing.breakdown} (room rate × nights)`}
                              >
                                <span className="hotel-card-pricing-label">
                                  Total Pricing
                                </span>
                                <strong className="hotel-card-pricing-value">
                                  {pricing.label}
                                </strong>
                                <span className="hotel-card-pricing-meta">
                                  {pricing.breakdown}
                                </span>
                              </div>
                            );
                          })()}
                          {(() => {
                            const total =
                              Number(
                                hotel.metrics?.totalRooms ?? hotel.totalRooms
                              ) || 0;
                            const occupied = Number(hotel.metrics?.occupied) || 0;
                            const occupiedPct = total
                              ? Math.round((occupied / total) * 100)
                              : 0;
                            const availablePct = total
                              ? Math.max(100 - occupiedPct, 0)
                              : 0;
                            return (
                              <div
                                className="hotel-card-occupancy-bar"
                                role="img"
                                aria-label={`${occupied} assigned, ${
                                  total - occupied
                                } available of ${total} rooms`}
                              >
                                <span
                                  className="hotel-card-occupancy-seg is-occupied"
                                  style={{ width: `${occupiedPct}%` }}
                                />
                                <span
                                  className="hotel-card-occupancy-seg is-available"
                                  style={{ width: `${availablePct}%` }}
                                />
                              </div>
                            );
                          })()}
                          <div className="acc-detail-actions hotel-card-actions">
                            <button
                              type="button"
                              className="btn hotel-card-preview-btn"
                              onClick={() => openPreviewFromHotel(hotel)}
                              title="Preview room layout"
                              aria-label="Preview room layout"
                            >
                              <i className="bi bi-eye" aria-hidden="true" />
                            </button>
                            <button
                              type="button"
                              className="btn edit-btn"
                              onClick={() => handleEdit(hotel)}
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              className="btn btn-danger"
                              onClick={() =>
                                setDeleteConfirmation({
                                  isOpen: true,
                                  hotelId: hotel._id,
                                  hotelName: hotel.hotelName,
                                })
                              }
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p>No hotels found.</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {addressModalOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content hotel-room-modal">
              <div className="modal-header">
                <h5 className="modal-title">Hotel Address</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeAddressModal}
                />
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Address Line 1</label>
                    <input
                      type="text"
                      className="form-control"
                      name="line1"
                      value={addressDraft.line1}
                      onChange={handleAddressDraftChange}
                      placeholder="Address Line 1"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Address Line 2</label>
                    <input
                      type="text"
                      className="form-control"
                      name="line2"
                      value={addressDraft.line2}
                      onChange={handleAddressDraftChange}
                      placeholder="Address Line 2"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">City</label>
                    <input
                      type="text"
                      className="form-control"
                      name="city"
                      value={addressDraft.city}
                      onChange={handleAddressDraftChange}
                      placeholder="City"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">State</label>
                    <input
                      type="text"
                      className="form-control"
                      name="state"
                      value={addressDraft.state}
                      onChange={handleAddressDraftChange}
                      placeholder="State"
                    />
                  </div>
                  <div className="col-md-4 mb-3">
                    <label className="form-label">Pincode</label>
                    <input
                      type="text"
                      className="form-control"
                      name="pincode"
                      value={addressDraft.pincode}
                      onChange={handleAddressDraftChange}
                      placeholder="Pincode"
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="form-label">Country</label>
                    <input
                      type="text"
                      className="form-control"
                      name="country"
                      value={addressDraft.country}
                      onChange={handleAddressDraftChange}
                      placeholder="Country"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeAddressModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-success btn-save"
                  onClick={saveAddressModal}
                >
                  Apply
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <HotelRoomPreviewModal
        isOpen={Boolean(preview)}
        onClose={() => setPreview(null)}
        viewMode={preview?.viewMode || "single"}
        eventName={selectedEvent?.eventName || ""}
        hotels={preview?.viewMode === "event" ? preview.hotels || [] : []}
        hotelName={preview?.viewMode === "single" ? preview.hotelName : ""}
        address={preview?.viewMode === "single" ? preview.address : undefined}
        rooms={preview?.viewMode === "single" ? preview.rooms || [] : []}
        metrics={preview?.viewMode === "single" ? preview.metrics : undefined}
        totalNights={
          preview?.viewMode === "single" ? preview.totalNights : undefined
        }
        isDraft={preview?.viewMode === "single" ? preview.isDraft : false}
      />

      {deleteConfirmation.isOpen && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Confirm Delete</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() =>
                    setDeleteConfirmation({
                      isOpen: false,
                      hotelId: null,
                      hotelName: "",
                    })
                  }
                />
              </div>
              <div className="modal-body">
                Are you sure you want to delete hotel:{" "}
                <strong>{deleteConfirmation.hotelName}</strong>?
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() =>
                    setDeleteConfirmation({
                      isOpen: false,
                      hotelId: null,
                      hotelName: "",
                    })
                  }
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={handleDelete}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HotelMaster;
